import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent
} from 'react';
import { InsulationLevelNumber } from 'utils/insulation-level-mappings';

interface InsulationSliderProps {
  value: InsulationLevelNumber | 'Custom';
  suggestedLevel?: InsulationLevelNumber;
  onChange: (level: InsulationLevelNumber) => void;
  levelTooltips?: Record<InsulationLevelNumber, string>;
  disabled?: boolean;
}

const LEVELS: InsulationLevelNumber[] = [1, 2, 3, 4, 5];
const COMMIT_THROTTLE_MS = 75;

function InsulationSlider({
  value,
  suggestedLevel,
  onChange,
  levelTooltips,
  disabled
}: InsulationSliderProps) {
  const isCustom = value === 'Custom';
  const isInactive = isCustom || disabled;
  const externalLevel: InsulationLevelNumber = isCustom ? 1 : value;

  const [localLevel, setLocalLevel] =
    useState<InsulationLevelNumber>(externalLevel);
  const isDraggingRef = useRef(false);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedRef = useRef<InsulationLevelNumber>(externalLevel);
  const localLevelRef = useRef<InsulationLevelNumber>(externalLevel);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    lastCommittedRef.current = externalLevel;
    if (!isDraggingRef.current) {
      localLevelRef.current = externalLevel;
      setLocalLevel(externalLevel);
    }
  }, [externalLevel]);

  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, []);

  const commitLevel = useCallback((level: InsulationLevelNumber) => {
    if (level === lastCommittedRef.current) return;
    lastCommittedRef.current = level;
    onChangeRef.current(level);
  }, []);

  const commitLatest = useCallback(() => {
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
    commitLevel(localLevelRef.current);
  }, [commitLevel]);

  const scheduleThrottledCommit = useCallback(() => {
    if (throttleTimerRef.current) return;
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null;
      commitLevel(localLevelRef.current);
    }, COMMIT_THROTTLE_MS);
  }, [commitLevel]);

  const handleInput = useCallback((event: FormEvent<HTMLInputElement>) => {
    const next = Number(
      (event.target as HTMLInputElement).value
    ) as InsulationLevelNumber;
    localLevelRef.current = next;
    setLocalLevel(next);
  }, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value) as InsulationLevelNumber;
    localLevelRef.current = next;
    setLocalLevel(next);
  }, []);

  const handlePointerDown = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
    commitLatest();
  }, [commitLatest]);

  const handleKeyUp = useCallback(() => {
    commitLatest();
  }, [commitLatest]);

  const handleKeyDown = useCallback(() => {
    scheduleThrottledCommit();
  }, [scheduleThrottledCommit]);

  const handleButtonClick = useCallback(
    (level: InsulationLevelNumber) => {
      localLevelRef.current = level;
      setLocalLevel(level);
      commitLevel(level);
    },
    [commitLevel]
  );

  const handleReset = useCallback(() => {
    if (suggestedLevel !== undefined) {
      localLevelRef.current = suggestedLevel;
      setLocalLevel(suggestedLevel);
      commitLevel(suggestedLevel);
    }
  }, [suggestedLevel, commitLevel]);

  const displayLevel: InsulationLevelNumber = isInactive ? 1 : localLevel;

  return (
    <div className='flex min-w-[280px] flex-col gap-1'>
      {isCustom && (
        <div className='flex items-center justify-center gap-2'>
          <span className='text-xs font-semibold text-amber-600'>
            Using fine-tuned values
          </span>
          {suggestedLevel && (
            <button
              type='button'
              onClick={handleReset}
              className='rounded bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white hover:bg-blue-600'
            >
              Reset
            </button>
          )}
        </div>
      )}

      <div
        className={`relative ${
          isInactive ? 'pointer-events-none opacity-40' : ''
        }`}
      >
        <input
          type='range'
          min={1}
          max={5}
          step={1}
          value={displayLevel}
          onInput={handleInput}
          onChange={handleChange}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          className='insulation-slider w-full cursor-pointer'
          disabled={isInactive}
        />

        <div className='mt-1 flex justify-between px-[2px]'>
          {LEVELS.map((level) => (
            <button
              key={level}
              type='button'
              onClick={() => handleButtonClick(level)}
              disabled={isInactive}
              title={levelTooltips?.[level]}
              className={`flex flex-col items-center gap-0.5 ${
                !isInactive && displayLevel === level
                  ? 'text-blue-600'
                  : 'text-gray-500'
              } ${
                !isInactive
                  ? 'cursor-pointer hover:text-blue-500'
                  : 'cursor-default'
              }`}
            >
              <span className='text-xs font-bold'>{level}</span>
            </button>
          ))}
        </div>
        <div className='flex justify-between px-[2px]'>
          <span className='text-[10px] text-gray-400'>Poor</span>
          <span className='text-[10px] text-gray-400'>Excellent</span>
        </div>
      </div>
    </div>
  );
}

export default memo(InsulationSlider);
